import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getBookingAmount } from '../utils/formatters';
import { useTheme } from '../contexts/ThemeContext';
import {
  Users, Building, Calendar, DollarSign, AlertCircle, ClipboardCheck, ArrowUpRight, ArrowDownRight, Activity,
  Clock, UserPlus, Home as HomeIcon, MoreHorizontal
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';

const Dashboard = () => {
  const { colors } = useTheme();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProperties: 0,
    approvedProperties: 0,
    pendingProperties: 0,
    totalBookings: 0,
    pendingReports: 0,
    revenue: 0,
    activeUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [monthlyData, setMonthlyData] = useState([]);
  const [propertyTypeData, setPropertyTypeData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const totalUsers = usersSnapshot.size;
      const activeUsers = usersSnapshot.docs.filter(doc => {
        const data = doc.data();
        return data.verificationStatus === 'verified';
      }).length;

      // Fetch properties with approval status
      const propertiesSnapshot = await getDocs(collection(db, 'properties'));
      const totalProperties = propertiesSnapshot.size;
      let approvedProperties = 0;
      let pendingProperties = 0;
      const typeCounts = { apartment: 0, house: 0, villa: 0, room: 0 };
      
      propertiesSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.approvalStatus === 'approved' || !data.approvalStatus) {
          approvedProperties++;
        } else if (data.approvalStatus === 'pending') {
          pendingProperties++;
        }
        if (data.type && typeCounts[data.type] !== undefined) {
          typeCounts[data.type]++;
        }
      });

      // Property type distribution
      setPropertyTypeData([
        { name: 'Apartment', value: typeCounts.apartment, color: '#3B82F6' },
        { name: 'House', value: typeCounts.house, color: '#22C55E' },
        { name: 'Villa', value: typeCounts.villa, color: '#F59E0B' },
        { name: 'Room', value: typeCounts.room, color: '#8B5CF6' },
      ].filter(item => item.value > 0));

      // Fetch bookings
      const bookingsSnapshot = await getDocs(collection(db, 'bookings'));
      const totalBookings = bookingsSnapshot.size;
      let revenue = 0;
      const monthlyBookings = {};
      
      bookingsSnapshot.forEach(doc => {
        const data = doc.data();
        const bookingAmount = getBookingAmount(data);
        revenue += bookingAmount;
        // Group by month
        const createdAt = data.createdAt?.toDate?.() || new Date();
        const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyBookings[monthKey]) {
          monthlyBookings[monthKey] = { bookings: 0, revenue: 0 };
        }
        monthlyBookings[monthKey].bookings++;
        monthlyBookings[monthKey].revenue += bookingAmount;
      });

      // Convert to chart data
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const chartData = [];
      for (let i = 0; i < 6; i++) {
        const date = new Date();
        date.setMonth(date.getMonth() - (5 - i));
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthData = monthlyBookings[monthKey] || { bookings: 0, revenue: 0 };
        chartData.push({
          month: months[date.getMonth()],
          bookings: monthData.bookings,
          revenue: monthData.revenue
        });
      }
      setMonthlyData(chartData);

      // Fetch pending reports
      let pendingReports = 0;
      try {
        const reportsSnapshot = await getDocs(
          query(collection(db, 'reports'), where('status', '==', 'pending'))
        );
        pendingReports = reportsSnapshot.size;
      } catch (e) {
        // Reports collection might not exist
      }

      // Fetch recent activities
      const activities = [];
      
      // Recent users
      const recentUsersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(3));
      const recentUsersSnapshot = await getDocs(recentUsersQuery);
      recentUsersSnapshot.forEach(doc => {
        const data = doc.data();
        activities.push({
          type: 'user',
          message: `New user registered: ${data.displayName || data.email}`,
          time: data.createdAt?.toDate?.() || new Date()
        });
      });

      // Recent properties
      const recentPropsQuery = query(collection(db, 'properties'), orderBy('createdAt', 'desc'), limit(3));
      const recentPropsSnapshot = await getDocs(recentPropsQuery);
      recentPropsSnapshot.forEach(doc => {
        const data = doc.data();
        activities.push({
          type: 'property',
          message: `New property listed: ${data.title || 'Untitled'}`,
          time: data.createdAt?.toDate?.() || new Date()
        });
      });

      // Sort by time
      activities.sort((a, b) => b.time - a.time);
      setRecentActivities(activities.slice(0, 5));

      setStats({
        totalUsers,
        totalProperties,
        approvedProperties,
        pendingProperties,
        totalBookings,
        pendingReports,
        revenue,
        activeUsers
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
      ghostBg: 'rgba(59, 130, 246, 0.1)',
      change: stats.activeUsers,
      changeLabel: 'verified',
      changePositive: true,
    },
    {
      title: 'Properties',
      value: stats.totalProperties,
      icon: Building,
      gradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
      ghostBg: 'rgba(34, 197, 94, 0.1)',
      change: stats.approvedProperties,
      changeLabel: 'approved',
      changePositive: true,
    },
    {
      title: 'Pending Approval',
      value: stats.pendingProperties,
      icon: ClipboardCheck,
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
      ghostBg: 'rgba(245, 158, 11, 0.1)',
      change: null,
      changeLabel: 'needs review',
      changePositive: false,
    },
    {
      title: 'Bookings',
      value: stats.totalBookings,
      icon: Calendar,
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
      ghostBg: 'rgba(139, 92, 246, 0.1)',
      change: null,
      changeLabel: 'total',
      changePositive: true,
    },
    {
      title: 'Revenue',
      value: `${stats.revenue.toLocaleString()} SAR`,
      icon: DollarSign,
      gradient: 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)',
      ghostBg: 'rgba(6, 182, 212, 0.1)',
      change: null,
      changeLabel: 'total revenue',
      changePositive: true,
    },
    {
      title: 'Pending Reports',
      value: stats.pendingReports,
      icon: AlertCircle,
      gradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
      ghostBg: 'rgba(239, 68, 68, 0.1)',
      change: null,
      changeLabel: 'needs attention',
      changePositive: false,
    },
  ];

  // Custom chart tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        className="px-4 py-3 rounded-xl shadow-lg"
        style={{
          backgroundColor: colors.surfaceElevated,
          border: `1px solid ${colors.border}`,
        }}
      >
        <p className="text-xs font-semibold mb-1" style={{ color: colors.text }}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: <span className="font-semibold">{entry.value.toLocaleString()}</span>
          </p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Skeleton Header */}
        <div>
          <div className="skeleton h-8 w-48 mb-2" style={{ backgroundColor: colors.border }} />
          <div className="skeleton h-4 w-72" style={{ backgroundColor: colors.border }} />
        </div>
        {/* Skeleton Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="rounded-2xl p-6 h-[140px]"
              style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
            >
              <div className="skeleton h-4 w-24 mb-4" style={{ backgroundColor: colors.border }} />
              <div className="skeleton h-7 w-16 mb-2" style={{ backgroundColor: colors.border }} />
              <div className="skeleton h-3 w-32" style={{ backgroundColor: colors.border }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight"
            style={{ color: colors.text }}
          >
            {getGreeting()} 👋
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Here's what's happening with your properties today.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-medium"
            style={{
              backgroundColor: colors.successGhost,
              color: colors.success,
            }}
          >
            <Activity className="w-3.5 h-3.5" />
            System Online
          </div>
          <div
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-medium"
            style={{
              backgroundColor: colors.surfaceHover,
              color: colors.textSecondary,
            }}
          >
            <Clock className="w-3.5 h-3.5" />
            {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className={`card-premium p-5 opacity-0 animate-fadeInScale stagger-${index + 1}`}
            style={{
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div
                className="flex items-center justify-center w-11 h-11 rounded-xl shadow-sm"
                style={{ background: stat.gradient }}
              >
                <stat.icon className="w-5 h-5 text-white" />
              </div>
              <button
                className="p-1 rounded-lg transition-colors"
                style={{ color: colors.textMuted }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.surfaceHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>

            <p
              className="text-[13px] font-medium mb-1"
              style={{ color: colors.textSecondary }}
            >
              {stat.title}
            </p>
            <p
              className="text-[26px] font-bold leading-tight tracking-tight"
              style={{ color: colors.text }}
            >
              {stat.value}
            </p>

            {/* Change Indicator */}
            <div className="flex items-center gap-1.5 mt-3">
              {stat.change !== null && (
                <span
                  className="flex items-center gap-0.5 text-[12px] font-semibold px-1.5 py-0.5 rounded-md"
                  style={{
                    backgroundColor: stat.changePositive ? colors.successGhost : colors.warningGhost,
                    color: stat.changePositive ? colors.success : colors.warning,
                  }}
                >
                  {stat.changePositive ? (
                    <ArrowUpRight className="w-3 h-3" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3" />
                  )}
                  {stat.change}
                </span>
              )}
              <span className="text-[12px]" style={{ color: colors.textMuted }}>
                {stat.changeLabel}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-7 gap-4 lg:gap-5">
        {/* Bookings Area Chart */}
        <div
          className="card-premium p-5 lg:col-span-4"
          style={{
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3
                className="text-[15px] font-semibold"
                style={{ color: colors.text }}
              >
                Booking Overview
              </h3>
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                Monthly booking trend
              </p>
            </div>
            <div
              className="flex items-center gap-4 text-[11px] font-medium"
              style={{ color: colors.textMuted }}
            >
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colors.primary }} />
                Bookings
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colors.success }} />
                Revenue
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={monthlyData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="bookingsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.primary} stopOpacity={0.2} />
                  <stop offset="100%" stopColor={colors.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={colors.chartGrid}
                vertical={false}
              />
              <XAxis
                dataKey="month"
                stroke={colors.textMuted}
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke={colors.textMuted}
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="bookings"
                stroke={colors.primary}
                strokeWidth={2.5}
                fill="url(#bookingsGradient)"
                dot={{ fill: colors.primary, r: 4, strokeWidth: 2, stroke: colors.card }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: colors.card }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Chart */}
        <div
          className="card-premium p-5 lg:col-span-3"
          style={{
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3
                className="text-[15px] font-semibold"
                style={{ color: colors.text }}
              >
                Revenue Trend
              </h3>
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                Monthly earnings (SAR)
              </p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthlyData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22C55E" stopOpacity={1} />
                  <stop offset="100%" stopColor="#16A34A" stopOpacity={0.8} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={colors.chartGrid}
                vertical={false}
              />
              <XAxis
                dataKey="month"
                stroke={colors.textMuted}
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke={colors.textMuted}
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="revenue"
                fill="url(#revenueGradient)"
                radius={[6, 6, 0, 0]}
                maxBarSize={36}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:gap-5">
        {/* Property Types – Donut Chart */}
        <div
          className="card-premium p-5 lg:col-span-2"
          style={{
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3
                className="text-[15px] font-semibold"
                style={{ color: colors.text }}
              >
                Property Distribution
              </h3>
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                By property type
              </p>
            </div>
          </div>
          {propertyTypeData.length > 0 ? (
            <div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={propertyTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={4}
                    dataKey="value"
                    strokeWidth={0}
                  >
                    {propertyTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {/* Legend */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                {propertyTypeData.map((entry, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={{ backgroundColor: colors.backgroundSecondary }}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-[12px] font-medium truncate" style={{ color: colors.textSecondary }}>
                      {entry.name}
                    </span>
                    <span className="text-[12px] font-bold ml-auto" style={{ color: colors.text }}>
                      {entry.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-[280px]">
              <Building className="w-10 h-10 mb-3" style={{ color: colors.textMuted }} />
              <p className="text-sm font-medium" style={{ color: colors.text }}>No properties yet</p>
              <p className="text-xs mt-1" style={{ color: colors.textMuted }}>Data will appear here</p>
            </div>
          )}
        </div>

        {/* Recent Activity – Timeline */}
        <div
          className="card-premium p-5 lg:col-span-3"
          style={{
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
          }}
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3
                className="text-[15px] font-semibold"
                style={{ color: colors.text }}
              >
                Recent Activity
              </h3>
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                Latest system events
              </p>
            </div>
          </div>
          <div className="space-y-1">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => {
                const isUser = activity.type === 'user';
                const IconComponent = isUser ? UserPlus : HomeIcon;
                const iconGradient = isUser
                  ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                  : 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)';

                return (
                  <div
                    key={index}
                    className="flex items-start gap-3.5 p-3 rounded-xl transition-colors group"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = colors.surfaceHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* Timeline Dot */}
                    <div className="relative flex-shrink-0">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm"
                        style={{ background: iconGradient }}
                      >
                        <IconComponent className="w-4 h-4 text-white" />
                      </div>
                      {/* Connector line */}
                      {index < recentActivities.length - 1 && (
                        <div
                          className="absolute left-1/2 top-full w-px h-4 -translate-x-1/2"
                          style={{ backgroundColor: colors.border }}
                        />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p
                        className="text-[13px] font-medium leading-snug"
                        style={{ color: colors.text }}
                      >
                        {activity.message}
                      </p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <Clock className="w-3 h-3" style={{ color: colors.textMuted }} />
                        <p className="text-[11px] font-medium" style={{ color: colors.textMuted }}>
                          {activity.time.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}{' '}
                          at{' '}
                          {activity.time.toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center py-10">
                <Activity className="w-10 h-10 mb-3" style={{ color: colors.textMuted }} />
                <p className="text-sm font-medium" style={{ color: colors.text }}>No recent activity</p>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                  Activities will appear here
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;